
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

@Component
export default class ScrollAnimatedTitle extends Vue {
  @Prop({ required: true, type: String }) private framePath!: string;
  @Prop({ required: true, type: String }) private posterSrc!: string;
  @Prop({ required: true, type: String }) private alt!: string;
  @Prop({ required: true, type: Number }) private progress!: number;
  @Prop({ required: true, type: Number }) private frameCount!: number;
  @Prop({ required: false, type: Number, default: 0.5 })
  private scrollStart!: number;
  @Prop({ required: false, type: Number, default: 1 })
  private scrollEnd!: number;
  @Prop({ required: false, type: Number, default: 2 })
  private easingPower!: number;

  private source = "";
  private framesReady = false;
  private frameRequest: number | null = null;
  private preloadObserver: IntersectionObserver | null = null;
  private frameObjectUrls: string[] = [];
  private currentFrame = -1;
  private loading = false;
  private componentDestroyed = false;

  private created() {
    this.source = this.posterSrc;
  }

  private mounted() {
    window.addEventListener("resize", this.queueFrame);

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.source = this.frameUrl(this.frameCount - 1);
      return;
    }

    if ("IntersectionObserver" in window) {
      this.preloadObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.preloadObserver?.disconnect();
            this.preloadObserver = null;
            this.preloadFrames();
          }
        },
        { rootMargin: "100% 0px" }
      );
      this.preloadObserver.observe(this.$el as Element);
    } else {
      this.preloadFrames();
    }
  }

  private frameUrl(index: number) {
    const base = process.env.BASE_URL || "/";
    const frame = String(index).padStart(5, "0");
    return `${base}title-frames/${this.framePath}/${frame}.webp`;
  }

  private async preloadFrames() {
    if (this.loading || this.framesReady) return;
    this.loading = true;
    this.frameObjectUrls = new Array(this.frameCount);
    let nextFrame = 0;

    const loadNext = async () => {
      while (nextFrame < this.frameCount && !this.componentDestroyed) {
        const index = nextFrame++;
        const response = await fetch(this.frameUrl(index));
        if (!response.ok) throw new Error(`Unable to load frame ${index}`);
        const blob = await response.blob();
        if (!this.componentDestroyed) {
          this.frameObjectUrls[index] = URL.createObjectURL(blob);
        }
      }
    };

    try {
      const concurrency = Math.min(8, this.frameCount);
      await Promise.all(Array.from({ length: concurrency }, loadNext));
      if (this.componentDestroyed) return;
      this.framesReady = true;
      this.queueFrame();
    } catch {
      // Leave the clean poster in place if a sequence cannot be downloaded.
    }
  }

  @Watch("progress")
  private onProgressChange() {
    this.queueFrame();
  }

  private queueFrame() {
    if (!this.framesReady || this.frameRequest !== null) return;

    this.frameRequest = window.requestAnimationFrame(() => {
      this.frameRequest = null;
      const slide = (this.$el as HTMLElement).closest(".slide");
      const slideTop = slide?.getBoundingClientRect().top;
      const slideProgress =
        typeof slideTop === "number"
          ? Math.min(
              Math.max((window.innerHeight - slideTop) / window.innerHeight, 0),
              1
            )
          : Math.min(Math.max(Number(this.progress) || 0, 0), 1);
      const linearProgress = Math.min(
        Math.max(
          (slideProgress - this.scrollStart) /
            Math.max(this.scrollEnd - this.scrollStart, 0.01),
          0
        ),
        1
      );
      const animationProgress = Math.pow(linearProgress, this.easingPower);
      const frame = Math.round(animationProgress * (this.frameCount - 1));

      if (frame !== this.currentFrame && this.frameObjectUrls[frame]) {
        this.currentFrame = frame;
        this.source = this.frameObjectUrls[frame];
      }
    });
  }

  private destroyed() {
    this.componentDestroyed = true;
    window.removeEventListener("resize", this.queueFrame);
    this.preloadObserver?.disconnect();
    if (this.frameRequest !== null) {
      window.cancelAnimationFrame(this.frameRequest);
    }
    this.frameObjectUrls.forEach((url) => {
      if (url) URL.revokeObjectURL(url);
    });
  }
}
